import { Authentication } from '@/modules/authentication/Authentication';
import { defineStore } from 'pinia';
import { io, Socket } from 'socket.io-client';
import { computed, ref, watch, watchEffect } from 'vue';
import { useUserStore } from './user/useUserStore';

interface OnlineUser {
  id: string;
  email: string;
  title: string;
  image: string;
  displayName: string;
  isEditing?: boolean;
}

interface PageOnlineStats {
  count: number;
  users: OnlineUser[];
}

type PageOnlineStatsMap = Record<string, PageOnlineStats>;

const notificationSocketUrl = (import.meta.env.VITE_NOTIFICATION_SOCKET_URL ?? '') + '/cfs-online';

export const useOnlineStore = defineStore('online', () => {
  const onlineUsers = ref<OnlineUser[]>([]);
  const isOnlineUsersDrawerOpen = ref(false);

  const onlineUsersCount = computed(() => {
    return onlineUsers.value.length;
  });

  const socket = ref<Socket | null>(null);
  const isConnectedToSocket = ref(false);

  const userStore = useUserStore();

  function connect() {
    if (socket.value) {
      disconnect();
    }
    socket.value = io(notificationSocketUrl, {
      path: '/ws/socket.io',
      reconnectionAttempts: 3,
      reconnectionDelay: 2000,
      extraHeaders: {
        Authorization: `Bearer ${Authentication.accessToken}`,
      },
    });
  }

  function disconnect() {
    if (!socket.value) {
      return;
    }
    socket.value.disconnect();
    socket.value = null;
  }

  watch(
    () => userStore.isAuthenticated,
    (isAuthenticated) => {
      isAuthenticated ? connect() : disconnect();
    },
    { immediate: true }
  );

  const pageOnlineStats = ref<PageOnlineStatsMap>();
  const pageOnlineUsers = ref<OnlineUser[]>([]);

  watchEffect(() => {
    if (!socket.value) {
      return;
    }

    socket.value.on('connect', () => {
      isConnectedToSocket.value = true;
    });

    socket.value.on('globalUserList', (data: OnlineUser[]) => {
      onlineUsers.value = data;
    });

    socket.value.on('userList', (data: OnlineUser[]) => {
      pageOnlineUsers.value = data;
    });

    socket.value.on('tenantStats', (stats: PageOnlineStatsMap) => {
      pageOnlineStats.value = stats;
    });

    socket.value.on('error', (error) => {
      console.error('notification ws error', error);
    });

    socket.value.on('reconnect_attempt', (attempt) => {
      console.log(`Trying to reconnect... Attempt #${attempt}`);
    });

    socket.value.on('reconnect_error', (error) => {
      console.error('Reconnection attempt failed:', error);
    });

    socket.value.on('disconnect', (e) => {
      console.log('notification ws disconnected', e);
    });

    socket.value.on('connect_error', (err: any) => {
      console.log(err.message, err.description);

      // In case of 400 (sticky cookies) kill the connection as it othwerwise will keep trying to reconnect indefenitely
      if (err?.message === 'xhr post error' && err?.description === 400) {
        // Disable further automatic reconnections
        if (socket.value) {
          socket.value.io.opts.reconnection = false;
        }
        socket.value?.disconnect();
      }
    });
  });

  function updateCurrentPage(page: string) {
    socket.value?.emit('page', page);
  }

  function startUserPageEdit() {
    socket.value?.emit('startEditing');
  }

  function stopUserPageEdit() {
    socket.value?.emit('stopEditing');
  }

  return {
    pageOnlineUsers,
    onlineUsers,
    pageOnlineStats,
    onlineUsersCount,
    isConnectedToSocket,
    isOnlineUsersDrawerOpen,
    updateCurrentPage,
    startUserPageEdit,
    stopUserPageEdit,
  };
});
